/**
 * UGC slider.
 *
 * Self-contained: no imports, no theme base classes, no shared slideshow component. The section
 * owns its own scroller, arrows, dots, drag handling and video playback so its behaviour can be
 * changed without tracking whatever Horizon does to its own carousels.
 *
 * The one concept in here is the *selected* clip — normally the one leading the viewport. Clicking a
 * tile, pressing an arrow, hitting a dot or using the keyboard all do the same thing: change the
 * selection. The caption panel, the dimming and playback all follow from it. Exactly one clip plays
 * at a time. Tile geometry never changes.
 */

/** How much of a tile has to be inside the viewport before it counts as still on screen. */
const VISIBLE_RATIO = 0.6;

/** How many refused play() calls before a tile is left on its poster instead of asked again. */
const PLAY_RETRY_LIMIT = 3;

/** Fallback retry delay, for when the element is already past 'canplay' and no event is coming. */
const PLAY_RETRY_DELAY = 400;

/**
 * How close the section has to be before any clip starts buffering. A screen ahead is enough for the
 * selected clip's opening seconds to arrive before the shopper does, and a shopper who never scrolls
 * that far never downloads it.
 */
const NEAR_MARGIN = '100% 0px';

const prefersReducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

class UgcSlider extends HTMLElement {
  /** @type {number} */
  #index = 0;

  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLElement[]} */
  #captions = [];

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  #inViewport = false;

  /** Within roughly a screen of the viewport, so a fast scroll does not outrun buffering. */
  #nearViewport = false;

  /**
   * Nothing buffers until the window has loaded, so clips never compete with the product gallery
   * for bandwidth while the page is still painting.
   */
  #pageLoaded = document.readyState === 'complete';

  /**
   * Reduced motion suppresses autoplay but not playback the shopper asked for. The first tap on a
   * tile or the sound button counts as asking.
   */
  #userInteracted = false;

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {IntersectionObserver | undefined} */
  #sectionObserver;

  /** @type {IntersectionObserver | undefined} */
  #nearObserver;

  /** Set while we are driving the scroller ourselves, so it does not fight the shopper's choice. */
  #programmaticScroll = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settleTimer;

  /** Clips with a play() already in flight, so a second call cannot abort the first. */
  #pending = new WeakSet();

  /** @type {WeakMap<HTMLVideoElement, number>} Refused play() attempts, per clip. */
  #retries = new WeakMap();

  /** @type {Set<ReturnType<typeof setTimeout>>} */
  #retryTimers = new Set();

  connectedCallback() {
    this.#build();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  /** Re-reads the DOM. Called on connect and whenever the theme editor re-renders the section. */
  #build() {
    this.#teardown();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.viewport = this.querySelector('.ugc-slider__viewport');
    this.#slides = [...this.querySelectorAll('.ugc-slide')];
    this.#captions = [...this.querySelectorAll('.ugc-slider__caption')];
    this.#dots = [...this.querySelectorAll('.ugc-slider__dot')];

    if (!this.viewport || !this.#slides.length) return;

    this.viewport.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    this.viewport.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener('visibilitychange', this.#updatePlayback, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

    if (!this.#pageLoaded) {
      window.addEventListener(
        'load',
        () => {
          this.#pageLoaded = true;
          this.#updatePlayback();
        },
        { once: true, signal }
      );
    }

    this.#setupDrag(signal);
    this.#observe();

    this.#index = Math.min(this.#index, this.#slides.length - 1);
    this.#render();
  }

  #teardown() {
    this.#controller?.abort();
    this.#sectionObserver?.disconnect();
    this.#nearObserver?.disconnect();
    clearTimeout(this.#settleTimer);
    for (const timer of this.#retryTimers) clearTimeout(timer);
    this.#retryTimers.clear();
    this.#programmaticScroll = false;
  }

  get count() {
    return this.#slides.length;
  }

  get selectedIndex() {
    return this.#index;
  }

  /**
   * Selects a clip.
   *
   * @param {number} index
   * @param {object} [options]
   * @param {boolean} [options.scroll] - Bring the clip into view. False when the selection is only
   *   catching up to where the shopper already scrolled.
   */
  select(index, { scroll = true } = {}) {
    const next = Math.max(0, Math.min(index, this.count - 1));
    if (next === this.#index) {
      if (scroll) this.#scrollTo(next);
      return;
    }

    this.#index = next;
    this.#render();
    if (scroll) this.#scrollTo(next);
  }

  previous() {
    this.select(this.#index - 1);
  }

  next() {
    this.select(this.#index + 1);
  }

  /**
   * Scrolls a clip to the leading edge of the viewport. Near the end of the track the scroller runs
   * out of room; that is fine, the clip stays selected either way.
   *
   * @param {number} index
   */
  #scrollTo(index) {
    const slide = this.#slides[index];
    if (!slide || !this.viewport) return;

    this.#programmaticScroll = true;
    this.viewport.scrollTo({
      left: slide.offsetLeft - this.viewport.offsetLeft,
      // 'instant' rather than 'auto' — 'auto' defers to the CSS scroll-behavior, which is smooth.
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * Selection is only reconsidered once the scroller has come to rest. Reacting to every scroll
   * event would let a smooth scroll we started ourselves overwrite the clip the shopper just chose,
   * before it had even arrived.
   */
  #onScroll = () => {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#onScrollSettled, 120);
  };

  #onScrollSettled = () => {
    if (this.#programmaticScroll) {
      this.#programmaticScroll = false;
    } else {
      this.#syncSelectionToScroll();
    }

    // Coming back to an already-selected clip changes no state, so #render never runs and nothing
    // else would ask it to play again. A tile that failed to start earlier gets its chance here.
    this.#updatePlayback();
  };

  /**
   * Scrolling never yanks the selection around: an explicitly chosen clip stays chosen while it is
   * still on screen. Only once it has been scrolled away does the selection fall to the clip now
   * leading the viewport, so the caption can never describe something the shopper cannot see.
   */
  #syncSelectionToScroll() {
    const selected = this.#slides[this.#index];
    if (selected && this.#isVisible(selected)) return;

    const leading = this.#leadingIndex();
    if (leading !== this.#index) this.select(leading, { scroll: false });
  }

  /**
   * Measured rather than observed, so it is correct on the very first scroll rather than whenever
   * an observer happens to report.
   *
   * @param {HTMLElement} slide
   * @returns {boolean}
   */
  #isVisible(slide) {
    if (!this.viewport) return false;

    const bounds = this.viewport.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    const overlap = Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left);

    return rect.width > 0 && overlap / rect.width >= VISIBLE_RATIO;
  }

  /** @returns {number} Index of the clip closest to the leading edge of the viewport. */
  #leadingIndex() {
    if (!this.viewport) return 0;

    const edge = this.viewport.scrollLeft;
    let best = 0;
    let bestDistance = Infinity;

    this.#slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - this.viewport.offsetLeft - edge);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });

    return best;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target) return;

    const sound = target.closest('.ugc-slide__sound');
    if (sound instanceof HTMLElement) {
      this.#userInteracted = true;
      this.#clearRefusals();
      this.#toggleSound(sound);
      return;
    }

    const control = target.closest('[data-ugc-action]');
    if (control instanceof HTMLElement) {
      const action = control.dataset.ugcAction;
      this.#userInteracted = true;
      this.#clearRefusals();

      if (action === 'previous') this.previous();
      else if (action === 'next') this.next();
      else if (action === 'select') this.select(this.#indexOf(control));

      this.#updatePlayback();
    }
  };

  /**
   * Dots carry their index outright; a tile's own select button reads it off DOM order.
   *
   * @param {HTMLElement} control
   * @returns {number}
   */
  #indexOf(control) {
    if (control.dataset.ugcIndex != null) return Number(control.dataset.ugcIndex);

    const slide = control.closest('.ugc-slide');
    return slide ? this.#slides.indexOf(/** @type {HTMLElement} */ (slide)) : 0;
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    event.preventDefault();
    this.#userInteracted = true;
    event.key === 'ArrowRight' ? this.next() : this.previous();
  };

  /** Keeps the theme editor in step when a merchant selects a clip in the sidebar. */
  #onBlockSelect = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement | null} */ (/** @type {any} */ (event).target);
    if (!target || !this.contains(target)) return;

    const index = this.#slides.indexOf(/** @type {HTMLElement} */ (target.closest('.ugc-slide')));
    if (index >= 0) this.select(index);
  };

  /**
   * Pointer dragging, so the carousel feels the same on a trackpad as it does on a phone. A drag
   * that actually moved swallows the click, otherwise letting go would also select a tile.
   *
   * @param {AbortSignal} signal
   */
  #setupDrag(signal) {
    const viewport = this.viewport;
    if (!viewport) return;

    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let moved = 0;

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'touch') return; // Native touch scrolling is better than ours.

        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = viewport.scrollLeft;
        moved = 0;
        viewport.classList.add('ugc-slider__viewport--grabbing');
      },
      { signal }
    );

    viewport.addEventListener(
      'pointermove',
      (event) => {
        if (pointerId !== event.pointerId) return;

        const delta = event.clientX - startX;
        if (Math.abs(delta) > 3 && !viewport.hasPointerCapture(pointerId)) {
          viewport.setPointerCapture(pointerId);
        }

        if (!viewport.hasPointerCapture(pointerId)) return;

        moved = Math.max(moved, Math.abs(delta));
        viewport.scrollLeft = startScroll - delta;
      },
      { signal }
    );

    const end = (/** @type {PointerEvent} */ event) => {
      if (pointerId !== event.pointerId) return;

      if (viewport.hasPointerCapture(pointerId)) viewport.releasePointerCapture(pointerId);
      viewport.classList.remove('ugc-slider__viewport--grabbing');
      pointerId = null;

      if (moved > 5) {
        viewport.addEventListener('click', (click) => { click.stopPropagation(); click.preventDefault(); }, {
          capture: true,
          once: true,
        });
      }
    };

    viewport.addEventListener('pointerup', end, { signal });
    viewport.addEventListener('pointercancel', end, { signal });
  }

  #observe() {
    this.#sectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.#inViewport = Boolean(entry?.isIntersecting);
        this.#updatePlayback();
      },
      { rootMargin: '100px' }
    );
    this.#sectionObserver.observe(this);

    this.#nearObserver = new IntersectionObserver(
      ([entry]) => {
        this.#nearViewport = Boolean(entry?.isIntersecting);
        this.#updatePlayback();
      },
      { rootMargin: NEAR_MARGIN }
    );
    this.#nearObserver.observe(this);
  }

  /** Pushes the current selection out to the tiles, the caption panel and the dots. */
  #render() {
    const selectedId = this.#slides[this.#index]?.dataset.blockId;

    this.#slides.forEach((slide, index) => {
      slide.dataset.active = String(index === this.#index);
    });

    for (const caption of this.#captions) {
      const isActive = caption.dataset.blockId === selectedId;
      caption.setAttribute('aria-hidden', String(!isActive));
      caption.toggleAttribute('inert', !isActive);
    }

    this.#dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', String(index === this.#index));
    });

    const previous = this.querySelector('[data-ugc-action="previous"]');
    const next = this.querySelector('[data-ugc-action="next"]');
    if (previous instanceof HTMLButtonElement) previous.disabled = this.#index === 0;
    if (next instanceof HTMLButtonElement) next.disabled = this.#index >= this.count - 1;

    this.#updatePlayback();
  }

  /**
   * @param {number} index
   * @returns {HTMLVideoElement | null}
   */
  #videoAt(index) {
    const video = this.#slides[index]?.querySelector('video');
    return video instanceof HTMLVideoElement ? video : null;
  }

  /**
   * Exactly one clip plays: the selected one. Everything else is paused on its poster, so the eye
   * has one place to go and the page is not decoding half a dozen videos at once.
   */
  #updatePlayback = () => {
    const suspended = this.#suspended();

    // Buffering is raised first. play() against an element still on preload="none" has no media
    // resource to start from, and that is the call most likely to be refused.
    this.#warmNeighbours();

    this.#slides.forEach((slide, index) => {
      const video = this.#videoAt(index);
      if (!video) return;

      const isSelected = index === this.#index;

      if (isSelected && !suspended) {
        if (video.paused) this.#play(video);
        return;
      }

      if (!video.paused) video.pause();

      // Suspending just pauses, so scrolling past the section and back picks up where it left off.
      // Losing the selection is different: that clip starts from the top next time, and stops talking.
      if (!isSelected) {
        if (video.currentTime) video.currentTime = 0;
        if (!video.muted) this.#mute(slide, video);
      }
    });
  };

  /**
   * A tap is a gesture, and a gesture is exactly what a browser refusing autoplay was waiting for.
   * Wipe the refusal history so the clip gets a clean set of attempts.
   */
  #clearRefusals() {
    for (const slide of this.#slides) {
      slide.removeAttribute('data-autoplay-blocked');
      const video = slide.querySelector('video');
      if (video instanceof HTMLVideoElement) this.#retries.delete(video);
    }
  }

  /** @returns {boolean} Whether autoplay should be held off entirely right now. */
  #suspended() {
    const motionAllowed = this.#userInteracted || !prefersReducedMotion();
    return document.hidden || !this.#inViewport || !motionAllowed;
  }

  /**
   * Starts a clip, and keeps asking if the browser refuses.
   *
   * iOS rejects play() routinely and transiently — AbortError when a pending play is interrupted by
   * the observer flipping mid-scroll, NotAllowedError under Low Power Mode. That rejected promise is
   * the only signal there is, so discarding it strands the tile on its poster with nothing scheduled
   * to rescue it.
   *
   * @param {HTMLVideoElement} video
   */
  #play(video) {
    // A second play() while one is in flight is what produces the AbortError in the first place.
    if (this.#pending.has(video)) return;

    if (video.preload === 'none') video.preload = 'auto';
    if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();

    const attempt = video.play();
    if (!attempt?.then) return;

    this.#pending.add(video);
    attempt.then(
      () => {
        this.#pending.delete(video);
        this.#retries.delete(video);
      },
      () => {
        this.#pending.delete(video);
        this.#retryLater(video);
      }
    );
  }

  /**
   * Re-asks once the clip has data, or after a short delay when it is already past 'canplay' and no
   * event is coming. Gives up after a few refusals: a browser that is blocking autoplay outright
   * will not change its mind, and the poster is the honest thing to show.
   *
   * @param {HTMLVideoElement} video
   */
  #retryLater(video) {
    const signal = this.#controller?.signal;
    if (!signal || signal.aborted) return;

    const attempts = (this.#retries.get(video) ?? 0) + 1;
    this.#retries.set(video, attempts);

    if (attempts > PLAY_RETRY_LIMIT) {
      video.closest('.ugc-slide')?.setAttribute('data-autoplay-blocked', 'true');
      return;
    }

    let done = false;
    const again = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      this.#retryTimers.delete(timer);
      if (this.#videoAt(this.#index) === video && !this.#suspended()) this.#play(video);
    };

    const timer = setTimeout(again, PLAY_RETRY_DELAY);
    this.#retryTimers.add(timer);
    video.addEventListener('canplay', again, { once: true, signal });
  }

  /**
   * With one clip playing at a time, the next one has to be ready the instant the carousel moves,
   * or every scroll would stall on a poster.
   *
   * Once the page has loaded and the section is within a screen of the viewport, the selected clip
   * and its neighbours buffer, so a shopper who scrolls down finds it already playing. The rest stay
   * unfetched, as does everything for a shopper who never scrolls that far. Clips that have already
   * started buffering are left alone.
   */
  #warmNeighbours() {
    if (!this.#pageLoaded || !this.#nearViewport || !this.#mayBufferEarly()) return;

    this.#slides.forEach((slide, index) => {
      const video = this.#videoAt(index);
      if (!video || video.preload === 'auto') return;

      if (Math.abs(index - this.#index) <= 1) video.preload = 'auto';
    });
  }

  /**
   * Buffering ahead of the viewport is a guess about what the shopper wants. Skip it for anyone who
   * asked to save data, or who will not get autoplay anyway.
   */
  #mayBufferEarly() {
    const connection = /** @type {any} */ (navigator).connection;
    if (connection?.saveData) return false;
    return this.#userInteracted || !prefersReducedMotion();
  }

  /**
   * Unmutes one clip and mutes every other, so two soundtracks can never overlap.
   *
   * @param {HTMLElement} button
   */
  #toggleSound(button) {
    const slide = button.closest('.ugc-slide');
    const video = slide?.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    const unmute = video.muted;

    for (const other of this.#slides) {
      const otherVideo = other.querySelector('video');
      if (otherVideo instanceof HTMLVideoElement) otherVideo.muted = true;
      this.#resetSoundButton(other);
    }

    if (!unmute) return;

    video.muted = false;
    button.setAttribute('aria-pressed', 'true');

    const label = button.querySelector('.ugc-slide__sound-label');
    if (label) label.textContent = button.dataset.labelOff || 'Turn sound off';

    video.play().catch(() => {});
  }

  /**
   * @param {HTMLElement} slide
   * @param {HTMLVideoElement} video
   */
  #mute(slide, video) {
    video.muted = true;
    this.#resetSoundButton(slide);
  }

  /** @param {Element} slide */
  #resetSoundButton(slide) {
    const button = slide.querySelector('.ugc-slide__sound');
    if (!(button instanceof HTMLElement)) return;

    button.setAttribute('aria-pressed', 'false');

    const label = button.querySelector('.ugc-slide__sound-label');
    if (label) label.textContent = button.dataset.labelOn || 'Turn sound on';
  }
}

if (!customElements.get('ugc-slider')) {
  customElements.define('ugc-slider', UgcSlider);
}

// The theme editor swaps whole sections in and out; rebuild ours when that happens.
document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {HTMLElement} */ (/** @type {any} */ (event).target);
  target?.querySelectorAll?.('ugc-slider').forEach((slider) => {
    if (slider.isConnected) slider.connectedCallback();
  });
});
